/**
 * Shared helpers for form component generation
 */

import { formFieldToZodType } from '@core-engine/schema/type-mappers/index.js'
import type { FormField, GeneratedFile, GeneratorOptions } from '@core-engine/schema/types.js'
import { singularize } from '@core-engine/utils/string.js'

export interface StepResult {
  files: GeneratedFile[]
}

export interface PublicFormComponentSourceOptions {
  actionImportPath?: string
  outputNamespace?: GeneratorOptions['outputNamespace']
}

/** Escape text for safe use in JSX content */
export function escapeJsx(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function escapeAttribute(str: string): string {
  return escapeJsx(str).replace(/"/g, '&quot;')
}

function jsString(value: string): string {
  return JSON.stringify(value)
}

/** Emit a native label line: visible when label is truthy, sr-only fallback to `name` when empty. */
export function formLabelLine(
  name: string,
  label: string,
  requiredStar: string,
  className?: string
): string {
  const classes = className ?? 'text-sm font-medium leading-none'
  if (!label)
    return `<label htmlFor="${escapeAttribute(name)}" className="sr-only">${escapeJsx(name)}${requiredStar}</label>`
  return `<label htmlFor="${escapeAttribute(name)}" className="${classes}">${label}${requiredStar}</label>`
}

function labelWithDescriptionLine(
  labelJSX: string,
  descriptionJSX: string,
  indent: string
): string {
  if (!descriptionJSX) return labelJSX
  return `<div className="flex flex-col gap-1">
${indent}  ${labelJSX}
${indent}  ${descriptionJSX}
${indent}</div>`
}

function formLabelWithDescriptionLine(
  name: string,
  label: string,
  requiredStar: string,
  descriptionJSX: string,
  indent: string,
  className?: string
): string {
  return labelWithDescriptionLine(
    formLabelLine(name, label, requiredStar, className),
    descriptionJSX,
    indent
  )
}

function fieldDescriptionLine(name: string, hint: string): string {
  if (!hint) return ''
  return `<p id="${escapeAttribute(name)}-description" className="text-sm text-muted-foreground">${escapeJsx(hint)}</p>`
}

function fieldContainerClass(height: FormField['height'], className = 'space-y-2'): string {
  if (height === 'fill') return `flex-1 min-h-0 ${className}`
  return className
}

function inputClass(height?: FormField['height']): string {
  const base =
    'h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm'
  return height === 'fill' ? `${base} flex-1` : base
}

function textareaClass(height?: FormField['height']): string {
  const base =
    'min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm'
  return height === 'fill' ? `${base} flex-1` : base
}

function selectClass(height?: FormField['height']): string {
  return inputClass(height)
}

function errorMessageLine(pathExpression: string, indent: string): string {
  return `${indent}{getFieldErrorMessage(form.formState.errors, ${pathExpression}) ? (
${indent}  <p className="text-sm text-destructive">
${indent}    {getFieldErrorMessage(form.formState.errors, ${pathExpression})}
${indent}  </p>
${indent}) : null}`
}

export function buildFieldErrorHelper(): string {
  return `function getFieldErrorMessage(errors: FieldErrors<FormValues>, path: string): string | undefined {
  let current: unknown = errors
  for (const segment of path.split('.')) {
    if (!current || typeof current !== 'object') {
      return undefined
    }
    current = (current as Record<string, unknown>)[segment]
  }
  if (current && typeof current === 'object' && 'message' in current) {
    const message = (current as { message?: unknown }).message
    return typeof message === 'string' ? message : undefined
  }
  return undefined
}`
}

/** Flatten form fields, resolving groups and skipping dynamic fields */
export function flattenFormFields(fields: FormField[]): FormField[] {
  return fields.flatMap((field) => {
    if (field.type === 'dynamicFields') return []
    if (field.type === 'group' && field.fields) return flattenFormFields(field.fields)
    return field.type === 'group' ? [] : [field]
  })
}

/** Build Zod schema field entries from form fields */
export function buildZodFields(fields: FormField[]): string {
  return fields
    .filter((f) => f.name)
    .map((f) => `  ${f.name}: ${formFieldToZodType(f)}`)
    .join(',\n')
}

/** Build default value entries from form fields */
export function buildDefaultValues(fields: FormField[]): string {
  return fields
    .filter((f) => f.name)
    .map((f) => {
      if (f.type === 'checkbox') {
        return f.options && f.options.length > 0 ? `      ${f.name}: []` : `      ${f.name}: false`
      }
      if (f.type === 'number') return `      ${f.name}: undefined`
      if (f.type === 'multiselect' || f.type === 'list') return `      ${f.name}: []`
      if (f.type === 'radio' && f.defaultValue !== undefined)
        return `      ${f.name}: ${jsString(String(f.defaultValue))}`
      if (f.type === 'select' || f.type === 'radio') return `      ${f.name}: undefined`
      if (f.defaultValue !== undefined)
        return `      ${f.name}: ${jsString(String(f.defaultValue))}`
      return `      ${f.name}: ''`
    })
    .join(',\n')
}

/** Build useFieldArray declarations for list fields */
export function buildFieldArrayDecls(listFields: FormField[]): string {
  return listFields
    .map(
      (f) =>
        `  const ${f.name}FieldArray = useFieldArray({ control: form.control, name: '${f.name}' })`
    )
    .join('\n')
}

/** Build watch variable declarations for showWhen fields */
export function buildWatchDecls(fields: FormField[]): { setup: string; hasWatch: boolean } {
  const watchFields = new Set<string>()
  for (const f of fields) {
    if (f.showWhen) {
      watchFields.add(f.showWhen.field)
    }
  }
  if (watchFields.size === 0) return { setup: '', hasWatch: false }
  const decls = Array.from(watchFields)
    .map((wf) => `  const ${wf}Value = form.watch('${wf}')`)
    .join('\n')
  return { setup: `\n${decls}\n`, hasWatch: true }
}

/** Get list fields that need useFieldArray */
export function getListFields(fields: FormField[]): FormField[] {
  return fields.filter((f) => f.name && f.type === 'list' && f.fields && f.fields.length > 0)
}

/**
 * Render an array of form fields to JSX, preserving group layout.
 * Group fields render as a responsive grid wrapper; other fields render normally.
 */
export function renderFieldsJSX(fields: FormField[]): string {
  return fields
    .filter((f) => !f.hidden)
    .map((f) => {
      if (f.type === 'dynamicFields') return ''
      if (f.type === 'group' && f.fields) {
        const cols = f.columns || 2
        const innerJSX = renderFieldsJSX(f.fields)
        const groupJSX = `        <div className="grid grid-cols-1 md:grid-cols-${cols} gap-5">\n${innerJSX}\n        </div>`
        return f.showWhen ? wrapShowWhen(f, groupJSX) : groupJSX
      }
      if (!f.name) return ''
      return wrapShowWhen(f, generateFieldJSX(f))
    })
    .filter(Boolean)
    .join('\n\n')
}

/** Wrap field JSX with showWhen conditional if applicable */
export function wrapShowWhen(field: FormField, jsx: string): string {
  if (!field.showWhen) return jsx
  const watchVar = `${field.showWhen.field}Value`
  const { value } = field.showWhen
  if (Array.isArray(value)) {
    const vals = value.map((v) => jsString(String(v))).join(', ')
    return `        {[${vals}].includes(${watchVar} as string) && (\n${jsx}\n        )}`
  }
  return `        {${watchVar} === ${jsString(String(value))} && (\n${jsx}\n        )}`
}

/** Generate JSX for a single form field */
export function generateFieldJSX(field: FormField): string {
  const name = field.name || ''
  const label = field.label ? escapeJsx(field.label) : ''
  const placeholder = field.placeholder || ''
  const hintJSX = fieldDescriptionLine(name, field.hint || '')
  const requiredStar = field.required ? ' <span className="text-destructive">*</span>' : ''

  switch (field.type) {
    case 'textarea':
      return `        <Controller
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <div className="${fieldContainerClass(field.height, field.height === 'fill' ? 'space-y-2 flex flex-col' : 'space-y-2')}">
              ${formLabelWithDescriptionLine(name, label, requiredStar, hintJSX, '              ')}
              <textarea
                id="${name}"
                placeholder="${placeholder}"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                className="${textareaClass(field.height)}"
              />
${errorMessageLine(jsString(name), '              ')}
            </div>
          )}
        />`

    case 'radio':
      if (field.options && field.options.length > 0) {
        const radioItems = field.options
          .map(
            (opt) =>
              `                  <label htmlFor="${escapeAttribute(`${name}-${opt.value}`)}" className="flex items-center gap-2 text-sm font-medium leading-none">
                    <input
                      type="radio"
                      id="${escapeAttribute(`${name}-${opt.value}`)}"
                      value="${escapeAttribute(opt.value)}"
                      checked={field.value === ${jsString(opt.value)}}
                      onChange={() => field.onChange(${jsString(opt.value)})}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                      className="size-4 accent-primary"
                    />
                    <span>${escapeJsx(opt.label)}</span>
                  </label>`
          )
          .join('\n')
        return `        <Controller
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <fieldset className="${fieldContainerClass(field.height, 'space-y-3')}">
              ${formLabelWithDescriptionLine(name, label, requiredStar, hintJSX, '              ')}
              <div className="flex flex-col gap-3">
${radioItems}
              </div>
${errorMessageLine(jsString(name), '              ')}
            </fieldset>
          )}
        />`
      }
      return generateTextFieldJSX(
        name,
        label,
        placeholder,
        hintJSX,
        requiredStar,
        'text',
        field.height
      )

    case 'select':
      if (field.options && field.options.length > 0) {
        const optionItems = field.options
          .map(
            (opt) =>
              `                <option value="${escapeAttribute(opt.value)}">${escapeJsx(opt.label)}</option>`
          )
          .join('\n')
        return `        <Controller
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <div className="${fieldContainerClass(field.height)}">
              ${formLabelWithDescriptionLine(name, label, requiredStar, hintJSX, '              ')}
              <select
                id="${name}"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                className="${selectClass(field.height)}"
              >
                <option value="">${escapeJsx(placeholder || 'Select...')}</option>
${optionItems}
              </select>
${errorMessageLine(jsString(name), '              ')}
            </div>
          )}
        />`
      }
      return generateTextFieldJSX(
        name,
        label,
        placeholder,
        hintJSX,
        requiredStar,
        'text',
        field.height
      )

    case 'multiselect':
      return generateMultiselectFieldJSX(field, name, label, hintJSX, requiredStar)

    case 'checkbox':
      if (field.options && field.options.length > 0) {
        const checkboxItems = field.options
          .map(
            (opt) =>
              `                  <label htmlFor="${escapeAttribute(`${name}-${opt.value}`)}" className="flex items-center gap-2 text-sm font-medium leading-none">
                    <input
                      type="checkbox"
                      id="${escapeAttribute(`${name}-${opt.value}`)}"
                      checked={Array.isArray(field.value) ? field.value.includes(${jsString(opt.value)}) : false}
                      onChange={(event) => {
                        const currentValue = Array.isArray(field.value) ? field.value : []
                        field.onChange(
                          event.target.checked
                            ? [...currentValue, ${jsString(opt.value)}]
                            : currentValue.filter((value) => value !== ${jsString(opt.value)})
                        )
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                      className="mt-0.5 size-4 rounded border-input accent-primary"
                    />
                    <span>${escapeJsx(opt.label)}</span>
                  </label>`
          )
          .join('\n')
        return `        <Controller
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <fieldset className="${fieldContainerClass(field.height, 'space-y-3')}">
              ${formLabelWithDescriptionLine(name, label, requiredStar, hintJSX, '              ')}
              <div className="flex flex-col gap-3">
${checkboxItems}
              </div>
${errorMessageLine(jsString(name), '              ')}
            </fieldset>
          )}
        />`
      }
      return `        <Controller
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <div className="${fieldContainerClass(field.height, 'flex flex-row items-start gap-3')}">
              <input
                type="checkbox"
                id="${name}"
                checked={Boolean(field.value)}
                onChange={(event) => field.onChange(event.target.checked)}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                className="mt-1 size-4 rounded border-input accent-primary"
              />
              <div className="space-y-2 leading-none">
                ${formLabelWithDescriptionLine(name, label || name, requiredStar, hintJSX, '                ')}
${errorMessageLine(jsString(name), '                ')}
              </div>
            </div>
          )}
        />`

    case 'email':
      return generateTextFieldJSX(
        name,
        label,
        placeholder || 'email@example.com',
        hintJSX,
        requiredStar,
        'email',
        field.height
      )

    case 'number':
      return generateNumberFieldJSX(name, label, placeholder, hintJSX, requiredStar, field.height)

    case 'date':
      return generateTextFieldJSX(
        name,
        label,
        placeholder,
        hintJSX,
        requiredStar,
        'date',
        field.height
      )

    case 'url':
      return generateTextFieldJSX(
        name,
        label,
        placeholder || 'https://',
        hintJSX,
        requiredStar,
        'url',
        field.height
      )

    case 'phone':
      return generateTextFieldJSX(
        name,
        label,
        placeholder || '+1 (555) 000-0000',
        hintJSX,
        requiredStar,
        'tel',
        field.height
      )

    case 'file':
    case 'upload':
      return generateFileUploadFieldJSX(field, name, label, hintJSX, requiredStar)

    case 'list':
      return generateListFieldJSX(field, name, label, hintJSX, requiredStar)

    default:
      return generateTextFieldJSX(
        name,
        label,
        placeholder,
        hintJSX,
        requiredStar,
        'text',
        field.height
      )
  }
}

function generateMultiselectFieldJSX(
  field: FormField,
  name: string,
  label: string,
  hintJSX: string,
  requiredStar: string
): string {
  const optionItems = (field.options || [])
    .map(
      (opt) =>
        `                <option value="${escapeAttribute(opt.value)}">${escapeJsx(opt.label)}</option>`
    )
    .join('\n')
  return `        <Controller
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <div className="${fieldContainerClass(field.height)}">
              ${formLabelWithDescriptionLine(name, label, requiredStar, hintJSX, '              ')}
              <select
                id="${name}"
                multiple
                value={Array.isArray(field.value) ? field.value : []}
                onChange={(event) => {
                  field.onChange(Array.from(event.target.selectedOptions, (option) => option.value))
                }}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                className="${selectClass(field.height)} min-h-24 py-2"
              >
${optionItems}
              </select>
${errorMessageLine(jsString(name), '              ')}
            </div>
          )}
        />`
}

export function generateTextFieldJSX(
  name: string,
  label: string,
  placeholder: string,
  hintJSX: string,
  requiredStar: string,
  inputType: string,
  height?: FormField['height']
): string {
  return `        <Controller
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <div className="${fieldContainerClass(height)}">
              ${formLabelWithDescriptionLine(name, label, requiredStar, hintJSX, '              ')}
              <input
                id="${name}"
                type="${inputType}"
                placeholder="${escapeAttribute(placeholder)}"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                className="${inputClass(height)}"
              />
${errorMessageLine(jsString(name), '              ')}
            </div>
          )}
        />`
}

function generateNumberFieldJSX(
  name: string,
  label: string,
  placeholder: string,
  hintJSX: string,
  requiredStar: string,
  height?: FormField['height']
): string {
  return `        <Controller
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <div className="${fieldContainerClass(height)}">
              ${formLabelWithDescriptionLine(name, label, requiredStar, hintJSX, '              ')}
              <input
                id="${name}"
                type="number"
                placeholder="${escapeAttribute(placeholder)}"
                value={field.value ?? ''}
                onChange={(event) => {
                  const nextValue = event.target.value === '' ? undefined : event.target.valueAsNumber
                  const normalizedValue = Number.isNaN(nextValue) ? undefined : nextValue
                  field.onChange(normalizedValue)
                }}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                className="${inputClass(height)}"
              />
${errorMessageLine(jsString(name), '              ')}
            </div>
          )}
        />`
}

function generateFileUploadFieldJSX(
  field: FormField,
  name: string,
  label: string,
  hintJSX: string,
  requiredStar: string
): string {
  const accept = field.accept || '*/*'
  const multiple = field.multiple ? '\n                multiple' : ''
  return `        <Controller
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <div className="${fieldContainerClass(field.height)}">
              ${formLabelWithDescriptionLine(name, label, requiredStar, hintJSX, '              ')}
              <input
                id="${name}"
                type="file"
                accept="${escapeAttribute(accept)}"${multiple}
                onChange={(event) => {
                  const selectedFiles = Array.from(event.target.files ?? [])
                  field.onChange(selectedFiles.map((file) => file.name).join(', '))
                }}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                className="${inputClass(field.height)}"
              />
${errorMessageLine(jsString(name), '              ')}
            </div>
          )}
        />`
}

function generateListFieldJSX(
  field: FormField,
  name: string,
  label: string,
  hintJSX: string,
  requiredStar: string
): string {
  if (!field.fields || field.fields.length === 0) {
    return generateTextFieldJSX(
      name,
      label,
      field.placeholder || '',
      hintJSX,
      requiredStar,
      'text',
      field.height
    )
  }

  const singularLabel = singularize(label || name)

  const nestedFieldsJSX = field.fields
    .map((nf) => {
      const nfName = nf.name || ''
      const nfLabelRaw = nf.label ? escapeJsx(nf.label) : nfName
      const nfPlaceholder = nf.placeholder || ''

      if (nf.type === 'select' && nf.options && nf.options.length > 0) {
        const selectItems = nf.options
          .map(
            (opt) =>
              `                          <option value="${escapeAttribute(opt.value)}">${escapeJsx(opt.label)}</option>`
          )
          .join('\n')
        return `                    <Controller
                      control={form.control}
                      name={\`${name}.\${index}.${nf.name}\`}
                      render={({ field }) => (
                        <div className="flex-1 space-y-2">
                          <label htmlFor={field.name} className="text-sm font-medium leading-none">${nfLabelRaw}</label>
                          <select
                            id={field.name}
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                            className="${selectClass()}"
                          >
                            <option value="">${escapeJsx(nf.placeholder || 'Select...')}</option>
${selectItems}
                          </select>
${errorMessageLine('field.name', '                          ')}
                        </div>
                      )}
                    />`
      }
      if (nf.type === 'number') {
        return `                    <Controller
                      control={form.control}
                      name={\`${name}.\${index}.${nf.name}\`}
                      render={({ field }) => (
                        <div className="flex-1 space-y-2">
                          <label htmlFor={field.name} className="text-sm font-medium leading-none">${nfLabelRaw}</label>
                          <input
                            id={field.name}
                            type="number"
                            placeholder="${escapeAttribute(nfPlaceholder)}"
                            value={field.value ?? ''}
                            onChange={(event) => {
                              const nextValue = event.target.value === '' ? undefined : event.target.valueAsNumber
                              const normalizedValue = Number.isNaN(nextValue) ? undefined : nextValue
                              field.onChange(normalizedValue)
                            }}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                            className="${inputClass()}"
                          />
${errorMessageLine('field.name', '                          ')}
                        </div>
                      )}
                    />`
      }

      return `                    <Controller
                      control={form.control}
                      name={\`${name}.\${index}.${nf.name}\`}
                      render={({ field }) => (
                        <div className="flex-1 space-y-2">
                          <label htmlFor={field.name} className="text-sm font-medium leading-none">${nfLabelRaw}</label>
                          <input
                            id={field.name}
                            type="text"
                            placeholder="${escapeAttribute(nfPlaceholder)}"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                            className="${inputClass()}"
                          />
${errorMessageLine('field.name', '                          ')}
                        </div>
                      )}
                    />`
    })
    .join('\n')

  const defaultObj = field.fields
    .map((nf) => {
      if (nf.type === 'select' || nf.type === 'radio') return `${nf.name}: undefined`
      if (nf.type === 'checkbox') {
        return nf.options && nf.options.length > 0 ? `${nf.name}: []` : `${nf.name}: false`
      }
      if (nf.type === 'number') return `${nf.name}: undefined`
      return `${nf.name}: ''`
    })
    .join(', ')

  return `        <div className="space-y-2">
          ${labelWithDescriptionLine(formLabelLine(name, label, requiredStar, 'text-sm font-medium leading-none'), hintJSX, '          ')}
          {${name}FieldArray.fields.map((item, index) => (
            <div key={item.id} className="relative rounded-sm border p-4">
              <button
                type="button"
                onClick={() => ${name}FieldArray.remove(index)}
                className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="size-4" />
                <span className="sr-only">Remove</span>
              </button>
              <div className="space-y-4 pr-8">
${nestedFieldsJSX}
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => ${name}FieldArray.append({ ${defaultObj} })}
            className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-xs transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
          >
            Add ${singularLabel}
          </button>
        </div>`
}
